import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { createSubmission, fetchCategories } from '../api/client';

const TYPES = [
  { code: 'T', label: 'Free / Open' },
  { code: 'R', label: 'Requires reg' },
  { code: 'M', label: 'Paid / Freemium' },
  { code: 'D', label: 'Dangerous / Underground' },
];

export default function SuggestModal({ open, onClose, onCreated }) {
  const [cats, setCats] = useState([]);
  const [form, setForm] = useState({
    name: '',
    url: '',
    category: '',
    subcategory: '',
    type: 'T',
    description: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    fetchCategories()
      .then((data) => {
        setCats(data);
        setForm((f) => ({ ...f, category: f.category || data[0]?.name || '' }));
      })
      .catch(() => setError('Failed to load categories'));
  }, [open]);

  if (!open) return null;

  const activeCat = cats.find((c) => c.name === form.category);
  const subs = activeCat?.subcategories || [];

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const payload = { ...form };
      if (!payload.subcategory) delete payload.subcategory;
      if (!payload.description) delete payload.description;
      const created = await createSubmission(payload);
      onCreated?.(created);
      setForm({ name: '', url: '', category: form.category, subcategory: '', type: 'T', description: '' });
      onClose();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : Array.isArray(detail)
          ? detail.map((d) => d.msg).join(', ')
          : 'Submission failed'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="notes-overlay" onClick={onClose}>
      <div className="notes-dialog suggest-dialog" onClick={(e) => e.stopPropagation()}>
        <div className="notes-header">
          <span>Suggest a Tool</span>
          <button className="icon-btn" onClick={onClose} aria-label="Close" type="button">
            <X size={16} />
          </button>
        </div>
        <form className="suggest-body" onSubmit={submit}>
          <label className="field">
            <span>Tool name *</span>
            <input
              type="text"
              required
              maxLength={80}
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="e.g. My Amazing AI"
            />
          </label>
          <label className="field">
            <span>URL *</span>
            <input
              type="url"
              required
              value={form.url}
              onChange={(e) => update('url', e.target.value)}
              placeholder="https://..."
            />
          </label>
          <div className="field-row">
            <label className="field">
              <span>Category *</span>
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                required
              >
                {cats.map((c) => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Subcategory</span>
              <input
                type="text"
                list="subcats"
                value={form.subcategory}
                onChange={(e) => update('subcategory', e.target.value)}
                placeholder="(optional)"
              />
              <datalist id="subcats">
                {subs.map((s) => (
                  <option key={s} value={s} />
                ))}
              </datalist>
            </label>
          </div>
          <div className="field">
            <span>Type</span>
            <div className="type-radios">
              {TYPES.map((t) => (
                <label key={t.code} className={`type-radio ${form.type === t.code ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="type"
                    value={t.code}
                    checked={form.type === t.code}
                    onChange={() => update('type', t.code)}
                  />
                  <span>({t.code}) {t.label}</span>
                </label>
              ))}
            </div>
          </div>
          <label className="field">
            <span>Description (optional)</span>
            <textarea
              rows={2}
              maxLength={280}
              value={form.description}
              onChange={(e) => update('description', e.target.value)}
              placeholder="Short description..."
            />
          </label>
          {error && <div className="error-msg">{error}</div>}
          <div className="form-actions">
            <button type="button" className="pill-btn" onClick={onClose}>Cancel</button>
            <button type="submit" className="pill-btn primary" disabled={submitting}>
              {submitting ? <Loader2 className="spin" size={14} /> : null}
              <span>Submit</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
